import { Box, Button, Chip, IconButton, Snackbar, TextField, Typography, useTheme } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { tokens } from '../../theme';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import Header from '../../components/Header';
import ConfirmDeleteDialog from '../../components/ConfirmDeleteDialog';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import FlightIcon from '@mui/icons-material/Flight';
import { can, filterActionColumns } from '../../utils/permissions';

const Excursion = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [openToast, setOpenToast] = useState(false);
  const [toastMessage, setToastmessage] = useState('');
  const [excursions, setExcursions] = useState([]);
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [sortModel, setSortModel] = useState([{ field: 'ExcursionID', sort: 'desc' }]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [idToDelete, setIdToDelete] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  const token = localStorage.getItem('token');

  // Debounce the search box so typing doesn't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const getData = async (signal) => {
    setLoading(true);

    const sortField = sortModel[0]?.field || 'ExcursionID';
    const sortOrder = sortModel[0]?.sort || 'asc';

    try {
      const res = await fetch(
        `${
          process.env.REACT_APP_APILINK
        }/excursion?page=${page}&limit=${pageSize}&sortBy=${sortField}&sortOrder=${sortOrder}&search=${encodeURIComponent(
          search
        )}`,
        { headers: { token }, signal }
      );

      const data = await res.json();
      if (data.success) {
        setExcursions(data?.excursions);
        setTotalCount(data?.pagination?.total);
      }
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error('Error fetching excursions:', err);
    }
    if (!signal?.aborted) setLoading(false);
  };

  useEffect(() => {
    const controller = new AbortController();
    getData(controller.signal);
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, sortModel, search]);

  const handleClearSearch = () => {
    setSearchInput('');
    setSearch('');
    setPage(1);
  };

  // Delete Excursion
  const handleDelete = async (id) => {
    const t = localStorage.getItem('token');
    const res = await fetch(`${process.env.REACT_APP_APILINK}/excursion/${id}`, {
      headers: { token: t },
      method: 'DELETE',
    });
    const data = await res.json();
    if (data.message) {
      setOpenToast(true);
      setToastmessage(data.message);
      if (data.success) {
        getData();
      }
    }
  };

  const openDeleteDialog = (id) => {
    setIdToDelete(id);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setIdToDelete(null);
  };

  const confirmDelete = async () => {
    if (idToDelete == null) return;
    await handleDelete(idToDelete);
    closeDeleteDialog();
  };

  const columns = [
    { field: 'ExcursionID', headerName: 'ID', hide: true, sortable: false, filterable: false },
    {
      field: 'ExcursionImage',
      headerName: 'Excursion Image',
      filterable: false,
      sortable: false,
      renderCell: (cellValues) => {
        return (
          <img
            src={cellValues.row.ExcursionImage}
            style={{ height: '100%', aspectRatio: '16/9', objectFit: 'cover', padding: '5px', maxWidth: '80px' }}
            alt={cellValues.row.ExcursionName}
          />
        );
      },
    },
    {
      field: 'ExcursionName',
      headerName: 'Name',
      flex: 1,
      minWidth: 120,
      cellClassName: 'name-column--cell',
    },
    {
      field: 'ExcursionLocation',
      headerName: 'Location',
      minWidth: 100,
      flex: 1,
      cellClassName: 'name-column--cell',
    },
    {
      field: 'ExcursionPrice',
      headerName: 'Price',
      flex: 1,
      minWidth: 100,
      valueFormatter: (params) => `€ ${params.value}`,
      cellClassName: 'name-column--cell',
    },
    {
      field: 'CountryName',
      headerName: 'Destination',
      flex: 1,
      minWidth: 100,
    },
    {
      field: 'Edit',
      filterable: false,
      sortable: false,
      renderCell: (cellValues) => {
        return (
          <Link to={`/excursions/${cellValues.row.ExcursionID}`}>
            <Button
              sx={{
                backgroundColor: colors.greenAccent[600],
                color: colors.grey[100],
                ':hover': { backgroundColor: colors.greenAccent[700] },
              }}
              variant="contained"
            >
              <EditIcon />
            </Button>
          </Link>
        );
      },
    },
    {
      field: 'Duplicate',
      sortable: false,
      filterable: false,
      renderCell: (cellValues) => {
        return (
          <Link to={`/excursions/${cellValues.row.ExcursionID}/duplicate`}>
            <Button
              sx={{
                backgroundColor: colors.grey[500],
                color: colors.grey[100],
                ':hover': { backgroundColor: colors.grey[600] },
              }}
              variant="contained"
            >
              <ContentCopyIcon />
            </Button>
          </Link>
        );
      },
    },
    {
      field: 'Gallery',
      sortable: false,
      filterable: false,
      renderCell: (cellValues) => {
        return (
          <Link to={`/excursions/${cellValues.row.ExcursionID}/gallery`}>
            <Button
              sx={{
                backgroundColor: colors.blueAccent[500],
                color: colors.grey[100],
                ':hover': { backgroundColor: colors.blueAccent[600] },
              }}
              variant="contained"
            >
              <VisibilityIcon />
            </Button>
          </Link>
        );
      },
    },
    {
      field: 'Delete',
      sortable: false,
      filterable: false,
      renderCell: (cellValues) => {
        return (
          <Button
            sx={{
              backgroundColor: colors.redAccent[500],
              color: colors.grey[100],
              ':hover': { backgroundColor: colors.redAccent[600] },
            }}
            variant="contained"
            onClick={() => openDeleteDialog(cellValues.row.ExcursionID)}
          >
            <DeleteIcon />
          </Button>
        );
      },
    },
  ];

  const handleSortModelChange = (newSortModel) => {
    setSortModel(newSortModel);
    setPage(1);
  };

  // Toast
  const handleCloseToast = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenToast(false);
  };

  const action = (
    <>
      <IconButton size="small" aria-label="close" color="inherit" onClick={handleCloseToast}>
        <CloseIcon fontSize="small" />
      </IconButton>
    </>
  );

  return (
    <Box m="20px">
      <Box display="flex" justifyContent="space-between" alignItems="center" gap="10px" flexWrap="wrap">
        <Header title="EXCURSIONS" subtitle="All Excursions" />

        {can('excursions.create') && (
          <Link to={'/excursions/addexcursion'}>
            <Button
              sx={{
                backgroundColor: colors.blueAccent[700],
                color: colors.grey[100],
                fontSize: '14px',
                fontWeight: 'bold',
                padding: '10px 20px',
                '&:hover': {
                  backgroundColor: colors.blueAccent[800],
                },
              }}
            >
              <FlightIcon sx={{ mr: '10px' }} />
              Add Excursion
            </Button>
          </Link>
        )}
      </Box>

      <Box display="flex" gap="10px" flexWrap="wrap" alignItems="center" m="10px 0 0 0">
        <TextField
          size="small"
          placeholder="Search by name, description, location, destination..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: colors.grey[300] }} />,
          }}
          sx={{ minWidth: '320px', backgroundColor: colors.primary[400] }}
        />
      </Box>

      {search && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 1, mb: 1 }}>
          <Typography variant="body2" sx={{ color: colors.grey[300], fontWeight: 500 }}>
            Active filters:
          </Typography>
          <Chip
            label={`Search: ${search}`}
            size="small"
            onDelete={handleClearSearch}
            sx={{ backgroundColor: colors.blueAccent[700], color: colors.grey[100], '& .MuiChip-deleteIcon': { color: colors.grey[300] } }}
          />
          {!loading && (
            <Typography variant="body2" sx={{ color: colors.greenAccent[400], ml: 1 }}>
              {totalCount} result{totalCount !== 1 ? 's' : ''}
            </Typography>
          )}
          <Typography
            variant="body2"
            onClick={handleClearSearch}
            sx={{
              color: colors.redAccent[500],
              cursor: 'pointer',
              ml: 1,
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            Clear all
          </Typography>
        </Box>
      )}

      <Box
        m="10px 0 0 0"
        height="667px"
        sx={{
          '& .MuiDataGrid-root': {
            border: 'none',
          },
          '& .MuiDataGrid-cell': {
            borderBottom: 'none',
          },
          '& .name-column--cell': {
            color: colors.grey[100],
          },
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: colors.blueAccent[700],
            borderBottom: 'none',
          },
          '& .MuiDataGrid-virtualScroller': {
            backgroundColor: colors.primary[400],
          },
          '& .MuiDataGrid-footerContainer': {
            borderTop: 'none',
            backgroundColor: colors.blueAccent[700],
          },
          '& .MuiCheckbox-root': {
            color: `${colors.greenAccent[200]} !important`,
          },
          '& .MuiDataGrid-toolbarContainer .MuiButton-text': {
            color: `${colors.grey[100]} !important`,
            fontSize: 14,
          },
        }}
      >
        <DataGrid
          rows={excursions}
          columns={filterActionColumns(columns, {
            Edit: 'excursions.edit',
            Duplicate: 'excursions.create',
            Delete: 'excursions.delete',
          })}
          getRowId={(row) => row.ExcursionID}
          disableColumnFilter
          page={page - 1}
          pageSize={pageSize}
          sortingOrder={['asc', 'desc']}
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          onPageChange={(newPage) => setPage(newPage + 1)}
          onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
          paginationMode="server"
          sortingMode="server"
          rowCount={totalCount}
          loading={loading}
        />
      </Box>

      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onClose={closeDeleteDialog}
        onConfirm={confirmDelete}
        title="Delete excursion?"
        message="Are you sure you want to delete this excursion?"
        colors={colors}
      />

      <Snackbar
        open={openToast}
        autoHideDuration={5000}
        onClose={handleCloseToast}
        message={toastMessage}
        action={action}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />
    </Box>
  );
};

export default Excursion;
